import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { strategicPartnershipsAlliances } from '../content/educational/strategicPartnershipsAlliances';

const processKeywords = (text, keywords) => {
  if (!keywords || !Array.isArray(keywords)) return text;
  
  let processedText = text;
  keywords.forEach(keyword => {
    if (keyword && typeof keyword === 'string') {
      const regex = new RegExp(`(${keyword})`, 'gi');
      processedText = processedText.replace(regex, '**$1**');
    }
  });
  return processedText;
};

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 35;
    const footerHeight = 12;
    const lineSpacing = 1.8;
    const paragraphSpacing = 0.6;
    const bulletIndent = 8;
    const numberIndent = 5;
    const titleSpacing = 12;
    const titleLineSpacing = 6;
    const sectionSpacing = 4;
    const contentSpacing = 3;
    const pageBreakThreshold = 10;

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    // Header component
    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow effect
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Expert Resources for Nonprofit Success', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    // Footer component
    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    };

    // Call to Action component
    const addCallToAction = (yPosition) => {
      const ctaHeight = 35;
      const qrSize = 22;
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 4, 4, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('Ready to start?', pageWidth / 2, yPosition + 13, { align: 'center' });

      doc.setFontSize(10);
      doc.text('Visit causeandimpact.org/apply', pageWidth / 2, yPosition + 22, { align: 'center' });

      doc.addImage(qrCodeApply, 'PNG', pageWidth - qrSize - margin - 6, yPosition + 6.5, qrSize, qrSize);
    };

    // Process markdown text
    const processMarkdown = (text) => {
      let segments = [];
      const boldRegex = /\*\*(.*?)\*\*/g;
      let lastIndex = 0;
      let match;
      
      while ((match = boldRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: text.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < text.length) {
        segments.push({
          text: text.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Process lists
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const bulletIndent = 12;
      const numberIndent = 8;
      const nestedIndent = 8;
      let previousLineWasHeader = false;
      
      const renderTextWithBold = (text, x, y, isAfterHeader = false) => {
        const segments = processMarkdown(text);
        let currentX = x;
        
        segments.forEach(segment => {
          doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
          if (isAfterHeader) {
            doc.setTextColor(37, 99, 235);
          } else {
            doc.setTextColor(31, 41, 55);
          }
          doc.text(segment.text, currentX, y);
          currentX += doc.getTextWidth(segment.text);
        });
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(31, 41, 55);
      };

      lines.forEach(line => {
        const isNested = line.startsWith('   ') || line.startsWith('\t');
        const baseIndent = isNested ? bulletIndent + nestedIndent : bulletIndent;
        const trimmedLine = line.trim();

        // Handle section headers
        if (trimmedLine.startsWith('###')) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(5, 150, 105);
          doc.text(trimmedLine.replace('###', '').trim(), margin, currentY);
          currentY += 6;
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(31, 41, 55); // Reset to default gray immediately after header
          previousLineWasHeader = true;
          return;
        } else {
          previousLineWasHeader = false;
        }

        // Handle numbered lists with bold titles
        const numberedMatch = trimmedLine.match(/^(\d+\))\s*\*\*(.*?)\*\*:\s*(.*)$/);
        if (numberedMatch) {
          const [, number, title, content] = numberedMatch;
          const x = margin + (isNested ? numberIndent + nestedIndent : numberIndent);
          
          // Render number
          doc.setFont('helvetica', 'normal');
          doc.text(number, x - 4, currentY);
          
          // Render bold title
          doc.setFont('helvetica', 'bold');
          doc.text(title + ':', x + 4, currentY);
          const titleWidth = doc.getTextWidth(title + ': ');
          
          // Render content
          doc.setFont('helvetica', 'normal');
          const contentLines = doc.splitTextToSize(content, contentWidth - (x + titleWidth + 8));
          contentLines.forEach((line, index) => {
            if (index === 0) {
              doc.text(line, x + titleWidth + 4, currentY);
            } else {
              currentY += 4;
              doc.text(line, x + 4, currentY);
            }
          });
          currentY += 6;
          return;
        }

        // Handle bullet points
        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
          doc.setTextColor(31, 41, 55); // Ensure default gray for bullet points
          const bulletText = trimmedLine.substring(1).trim();
          doc.text('•', margin + baseIndent, currentY);
          const processedBullet = processKeywords(bulletText, keywords);
          const bulletLines = doc.splitTextToSize(processedBullet, contentWidth - (baseIndent + 12));
          
          bulletLines.forEach((bLine) => {
            if (currentY + 4 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 12;
            }
            renderTextWithBold(bLine, margin + baseIndent + 8, currentY);
            currentY += 4;
          });
          currentY += 2;
        } else {
          // Regular text
          const lines = doc.splitTextToSize(trimmedLine, contentWidth);
          lines.forEach((line, index) => {
            // Only make the first line blue if it's right after a header
            if (index === 0 && previousLineWasHeader) {
              doc.setTextColor(37, 99, 235); // Blue for first line after header
              doc.text(line, margin, currentY);
              doc.setTextColor(31, 41, 55); // Reset to default gray for remaining text
            } else {
              doc.setTextColor(31, 41, 55); // Default gray for all other text
              doc.text(line, margin, currentY);
            }
            currentY += 4;
          });
          currentY += 2;
          previousLineWasHeader = false; // Reset header flag after processing the paragraph
        }
      });

      return currentY;
    };

    // Content generation
    const addContent = () => {
      let yPosition = headerHeight + 12;
      let pageNumber = 1;

      // Add title with increased spacing
      doc.setFontSize(20);
      doc.setTextColor(37, 99, 235);  // Set blue color
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 2, { align: 'center' });
      yPosition += (titleLines.length * 10) + 15; // Add more space after title
      
      content.forEach((section, index) => {
        // Check if we need a new page
        if (yPosition + 100 > pageHeight - footerHeight) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 12;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(5, 150, 105);  // Set green color
        doc.text(section.heading, margin, yPosition);
        yPosition += 12; // Increase spacing after section heading

        // Section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(31, 41, 55);  // Reset to default dark gray
        
        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          // Check for page break before each paragraph
          if (yPosition + 50 > pageHeight - footerHeight) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 12;
          }
          
          yPosition = processList(paragraph, yPosition);
          yPosition += 8; // Increase spacing between paragraphs
        });

        yPosition += 15; // Increase spacing between sections
      });

      // Add CTA
      const ctaHeight = 35;
      if (yPosition + ctaHeight > pageHeight - footerHeight - 12) {
        doc.addPage();
        pageNumber++;
        addHeader();
        addFooter(pageNumber);
        yPosition = headerHeight + 12;
      }
      
      yPosition += 10; // Add more space before CTA
      addCallToAction(yPosition);
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    // Add metadata
    doc.setProperties({
      title: title,
      subject: 'Strategic Partnerships and Alliances Guide',
      keywords: keywords.join(', '),
      author: 'Cause & Impact',
      creator: 'Cause & Impact',
      description: 'Comprehensive guide for building strategic partnerships and alliances in the nonprofit sector.',
      modDate: new Date()
    });

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

export const generateStrategicPartnershipsOverview = async () => {
  return generatePDF(
    strategicPartnershipsAlliances.sections,
    strategicPartnershipsAlliances.title,
    strategicPartnershipsAlliances.keywords
  );
}; 